/**
 * Stable row identifiers for floor-plan area rows and domain elements (RFC 4122 UUID v4).
 */

const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidV4String(value: string): boolean {
  return UUID_V4.test(value.trim());
}

/** UUID v4 using `globalThis.crypto.randomUUID` when available. */
export function newFloorPlanUuidV4(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const h = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Prefer canonical `uuid`, then legacy JSON key `id` when it is a valid v4 string;
 * if `id` is a non-v4 string, derive a deterministic v4 (same rule as {@link floorPlanElementUuid});
 * if there is no usable id/uuid, allocate a random v4.
 */
export function resolveFloorPlanDrawRowUuid(raw: Record<string, unknown>): string {
  const u = typeof raw['uuid'] === 'string' ? raw['uuid'].trim() : '';
  if (isUuidV4String(u)) {
    return u;
  }
  const legacy = typeof raw['id'] === 'string' ? raw['id'].trim() : '';
  if (isUuidV4String(legacy)) {
    return legacy;
  }
  if (legacy.length > 0) {
    return uuidV4FromSeedDeterministic(legacy);
  }
  return newFloorPlanUuidV4();
}

/**
 * Deterministic UUID v4 from an arbitrary seed (unit tests, legacy call sites).
 * Distinct seeds almost always yield distinct UUIDs.
 */
export function uuidV4FromSeedDeterministic(seed: string): string {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) {
    h = Math.imul(h ^ (h >>> 15), 2246822519);
    bytes[i] = (h >>> ((i % 4) * 8)) & 0xff;
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Canonical v4 string, or a deterministic v4 if `value` is not already v4. */
export function floorPlanElementUuid(value: string): string {
  return isUuidV4String(value) ? value.trim() : uuidV4FromSeedDeterministic(value);
}
